import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";

import { dashboardApi } from "../../api/dashboardApi";
import type { MachineChartsResponse } from "../../api/machineChartsApi";
import { ChartWidgetRenderer } from "../../components/charts/ChartWidgetRenderer";
import { normalizeTelemetryResponse } from "../../utils/telemetryParser";
import { useAuth } from "../../auth/AuthContext";
import "../../styles/ifi-galileo.css";

function toLocalInputValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(v: string) {
  return new Date(v);
}

function formatTickTime(v: string) {
  if (!v) return "";
  if (v.includes("T")) return v.slice(5, 16).replace("T", " ");
  return v.length > 14 ? v.slice(0, 14) + "…" : v;
}

export default function IfiGalileoAnalyticsPage() {
  const { machineId = "" } = useParams();
  const { token } = useAuth() as { token: string | null };

  const [fromLocal, setFromLocal] = useState(() => {
    const d = new Date(Date.now() - 7 * 24 * 3600 * 1000);
    return toLocalInputValue(d);
  });
  const [toLocal, setToLocal] = useState(() => toLocalInputValue(new Date()));

  const [machineName, setMachineName] = useState<string>("");
  const [chartsData, setChartsData] = useState<MachineChartsResponse | null>(null);
  const [tempSeries, setTempSeries] = useState<Array<{ t: string; value: number }>>([]);
  const [powerSeries, setPowerSeries] = useState<Array<{ t: string; value: number }>>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const fromISO = useMemo(() => fromLocalInputValue(fromLocal).toISOString(), [fromLocal]);
  const toISO = useMemo(() => fromLocalInputValue(toLocal).toISOString(), [toLocal]);

  useEffect(() => {
    if (!token || !machineId) return;
    dashboardApi
      .getDashboard(token)
      .then((dash) => {
        const md = (dash as any)?.machinesDashboard;
        const items = Array.isArray(md?.items) ? md.items : [];
        const row = items.find((m: any) => m.id === machineId);
        setMachineName(row?.name ?? row?.machineName ?? "");
      })
      .catch(() => setMachineName(""));
  }, [token, machineId]);

  const load = useCallback(async () => {
    if (!machineId) {
      setErr("ID macchina mancante.");
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const [chartsRes, tempRes, powerRes] = await Promise.allSettled([
        dashboardApi.getMachineCharts(token, machineId, fromISO, toISO),
        dashboardApi.getMachineTelemetry(token, machineId, {
          metric: "TEMPERATURE_C",
          from: fromISO,
          to: toISO,
          bucket: "1h",
        }),
        dashboardApi.getMachineTelemetry(token, machineId, {
          metric: "POWER",
          from: fromISO,
          to: toISO,
          bucket: "1h",
        }),
      ]);

      if (chartsRes.status === "fulfilled") setChartsData(chartsRes.value);
      else setChartsData(null);

      if (tempRes.status === "fulfilled") {
        const payload = (tempRes.value as any)?.data ?? tempRes.value;
        setTempSeries(normalizeTelemetryResponse(payload));
      } else setTempSeries([]);

      if (powerRes.status === "fulfilled") {
        const payload = (powerRes.value as any)?.data ?? powerRes.value;
        setPowerSeries(normalizeTelemetryResponse(payload));
      } else setPowerSeries([]);
    } catch (e: any) {
      setErr(e?.message ?? "Errore caricamento analytics");
    } finally {
      setLoading(false);
    }
  }, [token, machineId, fromISO, toISO]);

  useEffect(() => {
    load();
  }, [load]);

  const kpi = chartsData?.kpi;

  return (
    <div className="container-fluid ifi-galileo-detail-page">
      <div className="ifi-detail-hero mb-4">
        <div className="ifi-detail-hero-inner">
          <div className="ifi-detail-hero-brand">
            <div className="ifi-detail-hero-icon">
              <i className="bi bi-graph-up-arrow" />
            </div>
            <div>
              <h1 className="ifi-detail-hero-title">Analytics IFI Galileo</h1>
              <p className="ifi-detail-hero-sub">
                {machineName || "Macchina"}
                {machineId && <span className="ifi-detail-hero-id"> · ID {machineId}</span>}
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Link to={`/machines/IFI_GALILEO/${machineId}`} className="btn btn-light btn-sm">
              <i className="bi bi-info-circle me-1" />
              Dettaglio
            </Link>
            <Link to="/machines/IFI_GALILEO" className="btn ifi-detail-hero-btn">
              <i className="bi bi-arrow-left me-1" />
              Torna a IFI Galileo
            </Link>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label small text-muted mb-1">Da</label>
              <input
                type="datetime-local"
                className="form-control"
                value={fromLocal}
                onChange={(e) => setFromLocal(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label small text-muted mb-1">A</label>
              <input
                type="datetime-local"
                className="form-control"
                value={toLocal}
                onChange={(e) => setToLocal(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-4">
              <button
                type="button"
                className="btn btn-primary"
                disabled={loading}
                onClick={() => load()}
              >
                {loading ? "Caricamento…" : "Aggiorna"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {err && (
        <div className="alert alert-warning" role="alert">
          {err}
        </div>
      )}

      {(kpi?.avgTemp != null || kpi?.cyclesTotal != null) && (
        <div className="row g-3 mb-4">
          {kpi.avgTemp != null && (
            <div className="col-6 col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="small text-muted">Temp. media (periodo)</div>
                  <div className="fs-4 fw-bold text-primary">{kpi.avgTemp.toFixed(1)} °C</div>
                </div>
              </div>
            </div>
          )}
          {kpi.cyclesTotal != null && (
            <div className="col-6 col-md-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="small text-muted">Cicli (stima)</div>
                  <div className="fs-4 fw-bold">{kpi.cyclesTotal}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {chartsData?.widgets && chartsData.widgets.length > 0 && (
        <div className="row g-4 mb-4">
          {chartsData.widgets.map((w) => (
            <div key={w.id} className="col-12 col-lg-6">
              <div className="card shadow-sm h-100">
                <div className="card-header fw-semibold">{w.id}</div>
                <div className="card-body" style={{ minHeight: 320 }}>
                  <ChartWidgetRenderer widget={w as any} height={280} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-12 col-lg-7">
          <div className="card shadow-sm h-100">
            <div className="card-header fw-semibold">Temperatura media vetrine (TEMPERATURE_C)</div>
            <div className="card-body" style={{ height: 340, minHeight: 200, minWidth: 1 }}>
              {tempSeries.length ? (
                <ResponsiveContainer width="100%" height={340} minHeight={200} minWidth={1}>
                  <LineChart data={tempSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="t" tickFormatter={formatTickTime} minTickGap={24} />
                    <YAxis domain={["auto", "auto"]} unit=" °C" />
                    <Tooltip labelFormatter={(v) => String(v)} />
                    <Line type="monotone" dataKey="value" name="°C" stroke="#6366f1" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-muted py-5 text-center">
                  Nessun dato temperatura nel periodo selezionato.
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-5">
          <div className="card shadow-sm h-100">
            <div className="card-header fw-semibold">Energia (POWER)</div>
            <div className="card-body" style={{ height: 340, minHeight: 200, minWidth: 1 }}>
              {powerSeries.length ? (
                <ResponsiveContainer width="100%" height={340} minHeight={200} minWidth={1}>
                  <BarChart data={powerSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="t" tickFormatter={formatTickTime} minTickGap={24} />
                    <YAxis />
                    <Tooltip labelFormatter={(v) => String(v)} />
                    <Bar dataKey="value" name="kWh" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-muted py-5 text-center">
                  Nessun dato consumi nel periodo selezionato.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {chartsData?.meta && (
        <p className="small text-muted">
          Intervallo: {new Date(chartsData.meta.from).toLocaleString("it-IT")} —{" "}
          {new Date(chartsData.meta.to).toLocaleString("it-IT")}
        </p>
      )}
    </div>
  );
}
